import { useEffect, useRef } from "react";

import classnames from "classnames";
import styled from "styled-components";

import usePlayTo3DDataPrivateSources from "hooks/usePlayTo3DDataPrivateSources";
import useUserModules from "hooks/useUserModules";

import { ThreeDViewerOptionT } from "models";

import { visibilityToggleStyles } from "components/shared/SharedStyles";

import useMouseOver from "./context/useMouseover";
import {
  ThreeDVResetToggle,
  ThreeDVSettingsToggle,
  ThreeDVTopsToggle,
  ThreeDVVerticalPortionToggle,
  ThreeDVVerticalWellToggle,
  ThreeDVWellVolumeToggle
} from "./toolbar/3dv";
import VisFullscreenToggle from "./toolbar/VisFullscreenToggle";
import VisScreenshotToggle from "./toolbar/VisScreenshotToggle";
import { VisToolbarOverflow, useVisToolbarFitCount } from "./toolbar/VisToolbarOverflow";

interface ThreeDVToolbarProps {
  options: ThreeDViewerOptionT;
  mouseoverRef?: React.RefObject<HTMLDivElement>;
  resetCamera: () => void;
  onFullscreenToggle: (value) => void;
  /** Set by hosts that own their own expand, such as the well card panel. */
  hideFullscreen?: boolean;
  /** Which viewer the camera puts into screenshot mode. */
  screenshotWidgetId: string;
  /**
   * The host renders this strip inside a toolbar row of its own instead of
   * letting it hover over the scene. It drops the overlay chrome and the hover
   * gate: a bar the user can see has no reason to wait for a mouse.
   */
  embedded?: boolean;
  /**
   * The element the host gives the strip to live in. It is what decides how
   * much room there is, so it is what the fit is measured against.
   */
  hostElement?: Maybe<HTMLElement>;
}

function ThreeDVToolbar({
  options,
  mouseoverRef,
  resetCamera,
  onFullscreenToggle,
  hideFullscreen = false,
  embedded = false,
  hostElement = null,
  screenshotWidgetId
}: ThreeDVToolbarProps) {
  const Components = [
    <ThreeDVVerticalWellToggle key="verticalWell" {...options} />,
    <ThreeDVWellVolumeToggle key="wellVolume" {...options} />,
    <Divider key="divider" />,
    <ThreeDVResetToggle key="reset" onToggle={resetCamera} />,
    <ThreeDVTopsToggle key="tops" {...options} />,
    <ThreeDVVerticalPortionToggle key="verticalPortion" {...options} />,
    <VisScreenshotToggle
      key="screenshot"
      onToggle={onFullscreenToggle}
      type="ml"
      widgetId={screenshotWidgetId}
    />,
    <VisFullscreenToggle key="fullscreen" onToggle={onFullscreenToggle} />,
    <ThreeDVSettingsToggle key="settings" {...options} />
  ];

  const { has3dGeoModel } = useUserModules();

  const { data: privateDataSources, refetch: refetchPrivateDataSources } =
    usePlayTo3DDataPrivateSources();

  const geoModelEnabled = has3dGeoModel || !!privateDataSources?.length;

  // don't allow vertical well toggle if no access to 3d geo model
  if (!geoModelEnabled) {
    Components.splice(0, 1);
  }

  // After the index-based splice above, which assumes its own position.
  if (hideFullscreen) {
    const fullscreenAt = Components.findIndex((c) => c.key === "fullscreen");
    if (fullscreenAt >= 0) {
      Components.splice(fullscreenAt, 1);
    }
  }

  // Docked in a host's own toolbar row, the vertical rule reads as a divider
  // in that row rather than as a grouping inside this strip. Only the embedded
  // rendering drops it — a dashboard widget's overlay keeps it as it is.
  if (embedded) {
    const dividerAt = Components.findIndex((c) => c.key === "divider");
    if (dividerAt >= 0) {
      Components.splice(dividerAt, 1);
    }
  }

  useEffect(() => {
    refetchPrivateDataSources();
  }, []);

  const visible = useMouseOver(mouseoverRef);
  const wrapperClassnames = classnames({ visible: visible || embedded });

  // Docked in a host's bar the strip has a width to fit; floating over the
  // scene it does not, and every button stays inline exactly as before. This
  // strip fits at both panel widths today, so the menu is what keeps it that
  // way if the inventory or the font zoom grows.
  const stripRef = useRef<HTMLDivElement>(null);
  const fits = useVisToolbarFitCount({
    host: hostElement,
    stripRef,
    items: Components,
    enabled: embedded
  });
  // Nothing overflowing renders the array as it always did, untouched.
  const overflowing = fits < Components.length;
  const inline = overflowing ? Components.slice(0, fits) : Components;
  const overflowed = overflowing
    ? Components.slice(fits).filter((item) => String(item.key) !== "divider")
    : [];

  return (
    <Wrapper ref={stripRef} className={wrapperClassnames} $embedded={embedded}>
      {inline}
      {overflowed.length > 0 && <VisToolbarOverflow items={overflowed} />}
    </Wrapper>
  );
}

export default ThreeDVToolbar;

const Wrapper = styled.div<{ $embedded?: boolean }>`
  ${visibilityToggleStyles};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 0 8px;
  height: var(--chart-toolbar-height);
  display: flex;
  align-items: center;
  justify-content: right;
  gap: 8px;
  background-color: var(--white);
  box-shadow: 0 14px 16px 0 rgb(45 53 63 / 10%),
    inset 0px 7px 8px -10px rgb(45 53 63 / 15%);
  z-index: 1201;
  padding-right: 4px;

  /* Sits in the host's bar: no lift off the scene, no fade, nothing to
     raise. It stays one row at the host's width; the buttons that do not fit
     go behind the overflow menu instead of wrapping or being clipped.

     NOTE: no backticks in this comment — it sits inside a
     styled-components template literal, and one would terminate it. */
  ${(props) =>
    props.$embedded &&
    `
    position: static;
    height: 100%;
    padding: 0;
    background-color: transparent;
    box-shadow: none;
    transition: none;
    z-index: auto;
  `}
`;

const Divider = styled.div`
  width: 1px;
  height: var(--chart-toolbar-height);
  background-color: #d9e1e2;
`;
